import { useEffect, useState } from "react";

import { listCableRunsForRevision, type CableRunChoice } from "../services/earthFaultLoop";

// The cable-run selector of an earth-fault loop circuit row (EOS-05d, A33, D5): the cable
// runs stored under the active open revision, listed from the cable runs API. The chosen run's
// conductor figures are read by the backend when the study is calculated; nothing is shown
// from the stored result here, so the row's figures on the result page are the only truth.

/** The first option: nothing is chosen for the engineer. */
const CHOOSE = "Choose…";

export const NO_CABLE_RUNS_NOTE = "This revision holds no cable run yet.";

export const CABLE_RUN_HINT =
  "The line and protective conductor areas, the material, the route length, the parallel " +
  "runs, the insulation's maximum temperature and the reactance are read from the stored " +
  "cable run when the study is calculated.";

/** "CBL-C-01 · rev 2 · Design check passed" */
export function cableRunOptionText(run: CableRunChoice): string {
  const status = run.design_check_status.replaceAll("_", " ").toLowerCase();
  return `${run.calculation_key} · rev ${run.revision_number} · ${status.charAt(0).toUpperCase()}${status.slice(1)}`;
}

type CableRunSelectorProps = {
  /** The active open revision, or null (the form shows the no-revision note itself). */
  revisionId: string | null;
  /** The field name, stable per row ("circuit-3-cableRunId"). */
  name: string;
  /** The run kept in the draft, "" when none. */
  cableRunId: string;
  onChange: (cableRunId: string) => void;
};

export function CableRunSelector({ revisionId, name, cableRunId, onChange }: CableRunSelectorProps) {
  const [cableRuns, setCableRuns] = useState<CableRunChoice[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  useEffect(() => {
    if (revisionId === null) {
      setCableRuns(null);
      setListError(null);
      return;
    }
    const controller = new AbortController();
    setCableRuns(null);
    setListError(null);
    listCableRunsForRevision(revisionId, controller.signal)
      .then((runs) => {
        if (!controller.signal.aborted) {
          setCableRuns(runs);
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setListError(error instanceof Error ? error.message : "The cable runs could not be listed.");
        }
      });
    return () => controller.abort();
  }, [revisionId]);

  // A run kept in the draft that the listed revision does not hold is no longer chosen.
  useEffect(() => {
    if (cableRuns !== null && cableRunId !== "" && !cableRuns.some((run) => run.id === cableRunId)) {
      onChange("");
    }
  }, [cableRuns, cableRunId, onChange]);

  return (
    <>
      <label>
        Cable run
        <select name={name} value={cableRunId} onChange={(event) => onChange(event.target.value)}>
          <option value="">{CHOOSE}</option>
          {(cableRuns ?? []).map((run) => (
            <option key={run.id} value={run.id}>
              {cableRunOptionText(run)}
            </option>
          ))}
        </select>
      </label>
      {cableRuns !== null && cableRuns.length === 0 ? (
        <p data-no-cable-runs-note="true">{NO_CABLE_RUNS_NOTE}</p>
      ) : null}
      {listError !== null ? <p role="alert">{listError}</p> : null}
      <p data-cable-run-hint="true">{CABLE_RUN_HINT}</p>
    </>
  );
}

export default CableRunSelector;
